import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Container } from '@mui/material';

const COUNTER_KEYS = ['channels', 'availability', 'aiRate', 'savings'];

const CounterSection = () => {
  const { t } = useTranslation('landing');
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [displayValues, setDisplayValues] = useState({});

  // Animate a single numeric counter from 0 to target
  const animateCount = useCallback((key, target, duration = 1200) => {
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);

      setDisplayValues((prev) => ({ ...prev, [key]: String(current) }));

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            COUNTER_KEYS.forEach((key) => {
              const rawValue = t(`counters.${key}.value`);

              // "24/7" is not numeric -- display directly, no animation
              if (rawValue.includes('/')) {
                setDisplayValues((prev) => ({ ...prev, [key]: rawValue }));
              } else {
                const numeric = parseInt(rawValue, 10);
                if (!isNaN(numeric)) {
                  animateCount(key, numeric);
                } else {
                  setDisplayValues((prev) => ({ ...prev, [key]: rawValue }));
                }
              }
            });

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [hasAnimated, t, animateCount]);

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        background: 'linear-gradient(135deg, #3664D1, #1a3a8a)',
        clipPath: 'polygon(0 0, 100% 24px, 100% calc(100% - 24px), 0 100%)',
        py: { xs: '60px', md: '80px' },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: '24px', md: '32px' },
            textAlign: 'center',
          }}
        >
          {COUNTER_KEYS.map((key) => {
            const suffix = t(`counters.${key}.suffix`);
            const label = t(`counters.${key}.label`);
            const value = displayValues[key] || '0';

            return (
              <Box key={key}>
                {/* Value + suffix */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: '40px', md: '52px' },
                      fontWeight: 900,
                      color: '#fff',
                      lineHeight: 1,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {value}
                  </Typography>
                  {suffix && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '16px', md: '20px' },
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.8)',
                      }}
                    >
                      {suffix}
                    </Typography>
                  )}
                </Box>

                {/* Label */}
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '16px' },
                    color: '#fff',
                    opacity: 0.55,
                    mt: 0.5,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export { CounterSection };
